/**
 * Provides a hook to manage a JSON value in sessionStorage or localStorage.
 * 
 * Manages the serialization and deserialization of JSON values to and from strings.
 * 
 * @packageDocumentation
 * @module useStorageJSON
 * @public
 */


// TODO: add support for non-json strings

import { useCallback, useEffect, useRef, useState } from 'react';

// eslint-disable-next-line @typescript-eslint/no-unused-vars
export type UseStorageJSONOptions<T> = {
  storage?: Storage;
  onError?: (error: unknown) => void;
};

function serialize<T>(
  value: T,
  onError: (error: unknown) => void
): string | null {
  try {
    return JSON.stringify(value);
  } catch (error) {
    onError(error);
    return null;
  }
}

function deserialize<T>(
  raw: string,
  onError: (error: unknown) => void
): T | null {
  try {
    return JSON.parse(raw) as T;
  } catch (error) {
    onError(error);
    return null;
  }
}

/**
 * Monitors and synchronizes a storage key, serializing and deserializing
 * values as JSON so non-string values can be stored and shared.
 *
 * Reacts to changes dispatched via the storage event and polls every 100 ms to
 * detect direct mutations against the configured storage object.
 *
 * @template T - The type of the stored value.
 * @param key - The storage key to monitor.
 * @param initialValue - Value used when no stored entry is found or when
 *   deserialization fails.
 * @param options - Optional configuration.
 * @param options.storage - Storage backend to use. Defaults to sessionStorage.
 * @param options.onError - Called on JSON serialization or deserialization
 *   errors. Defaults to console.error.
 * @returns A tuple of the current deserialized value and a setter that
 *   serializes and persists the new value.
 *
 * @example
 * const [filters, setFilters] = useStorageJSON(
 *   'user-preferences',
 *   { pinAppBar: false },
 *   { storage: localStorage }
 * );
 */
export function useStorageJSON<T>(
  key: string,
  initialValue: T,
  options?: UseStorageJSONOptions<T>
): [T, (newValue: T) => void] {
  const storage = options?.storage ?? sessionStorage;

  const onErrorRef = useRef(options?.onError);
  useEffect(() => {
    onErrorRef.current = options?.onError;
  }, [options?.onError]);

  const handleError = useCallback((error: unknown) => {
    if (onErrorRef.current !== undefined) {
      onErrorRef.current(error);
      return;
    }

    console.error(error);
  }, []);

  const getStoredValue = (): T => {
    const stored = storage.getItem(key);
    if (stored === null) return initialValue;
    return deserialize<T>(stored, handleError) ?? initialValue;
  };

  const [value, setValue] = useState<T>(getStoredValue);

  const setStoredValue = useCallback(
    (newValue: T) => {
      const serialized = serialize(newValue, handleError);
      if (serialized === null) return;

      const oldValue = storage.getItem(key);
      setValue(newValue);
      try {
        storage.setItem(key, serialized);
      } catch {
        // Ignore quota errors; in-memory state remains authoritative.
      }

      window.dispatchEvent(
        new StorageEvent('storage', {
          key,
          newValue: serialized,
          oldValue,
          storageArea: storage,
        })
      );
    },
    [handleError, key, storage]
  );

  useEffect(() => {
    const handleStorage = (event: StorageEvent) => {
      if (event.storageArea !== storage || event.key !== key) return;

      if (event.newValue === null) {
        setValue(initialValue);
        return;
      }

      setValue(deserialize<T>(event.newValue, handleError) ?? initialValue);
    };

    window.addEventListener('storage', handleStorage);
    return () => {
      window.removeEventListener('storage', handleStorage);
    };
  }, [handleError, initialValue, key, storage]);

  useEffect(() => {
    let lastRaw = storage.getItem(key);

    const interval = setInterval(() => {
      const currentRaw = storage.getItem(key);
      if (currentRaw !== lastRaw) {
        lastRaw = currentRaw;
        setValue(
          currentRaw !== null
            ? deserialize<T>(currentRaw, handleError) ?? initialValue
            : initialValue
        );
      }
    }, 100);

    return () => clearInterval(interval);
  }, [handleError, initialValue, key, storage]);

  return [value, setStoredValue];
}

export function useSessionStorageJSON<T>(
  key: string,
  initialValue: T,
  options?: Omit<UseStorageJSONOptions<T>, 'storage'>
): [T, (newValue: T) => void] {
  return useStorageJSON(key, initialValue, {
    ...options,
    storage: sessionStorage,
  });
}

export function useLocalStorageJSON<T>(
  key: string,
  initialValue: T,
  options?: Omit<UseStorageJSONOptions<T>, 'storage'>
): [T, (newValue: T) => void] {
  return useStorageJSON(key, initialValue, {
    ...options,
    storage: localStorage,
  });
}
